import api from './api'

export interface Viatger {
  id: string
  reserva_id: string

  // Informació personal
  nom: string
  cognoms: string
  email?: string | null
  telefon?: string | null
  mobil?: string | null

  // Documentació
  tipus_document: string // DNI, Passport, NIE, etc.
  num_document: string
  nacionalitat?: string | null
  data_naixement?: string | null

  // Adreça
  adreca?: string | null
  ciutat?: string | null
  provincia?: string | null
  codi_postal?: string | null
  pais?: string | null

  // Informació adicional
  empresa?: string | null
  titol?: string | null // Mr, Mrs, Ms, etc.
  peticions_especials?: string | null

  // Estat
  es_titular: boolean
  consentiment_rgpd: boolean
  consentiment_marketing: boolean
  data_consentiment?: string | null

  // Metadata
  created_at: string
  updated_at: string

  // Dates de la reserva
  reserva_data_entrada?: string | null
  reserva_data_sortida?: string | null

  // Relació amb reserva (opcional si s'inclou)
  reserva?: {
    id: string
    guest_name: string
    data_entrada: string
    data_sortida: string
  }
}

export interface ViatgersResponse {
  data: Viatger[]
  meta: {
    current_page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface ViatgerResponse {
  success: boolean
  data?: Viatger
  message?: string
  errors?: Record<string, string[]>
}

export interface EstadistiquesResponse {
  total_viatgers: number
  total_titular: number
  per_nacionalitat: Record<string, number>
  per_tipus_document: Record<string, number>
  per_mes: Record<string, number>
}

class ViatgersApiService {
  async getAll(params: any = {}): Promise<ViatgersResponse> {
    try {
      const response = await api.get('/viatgers', { params })
      return response.data
    } catch (error: any) {
      console.error('Error fetching viatgers:', error)
      throw error
    }
  }

  async getById(id: string): Promise<ViatgerResponse> {
    try {
      const response = await api.get(`/viatgers/${id}`)
      // Si la respuesta es exitosa, el backend devuelve el viatger directamente
      // Lo envolvemos en el formato esperado
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error fetching viatger:', error)
      throw error
    }
  }

  async getByReservation(reservaId: string): Promise<Viatger[]> {
    try {
      const response = await api.get(`/viatgers?reserva_id=${reservaId}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching viatgers by reservation:', error)
      throw error
    }
  }

  async create(viatgerData: Partial<Viatger>): Promise<ViatgerResponse> {
    try {
      const response = await api.post('/viatgers', viatgerData)
      // Si la respuesta es exitosa, el backend devuelve el viatger creado directamente
      // Lo envolvemos en el formato esperado
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error creating viatger:', error)

      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }

      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en crear el viatger'
        }
      }

      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async update(id: string, viatgerData: Partial<Viatger>): Promise<ViatgerResponse> {
    try {
      // Eliminar reserva_id del payload (no es pot canviar en updates)
      const { reserva_id, ...updateData } = viatgerData
      
      const response = await api.put(`/viatgers/${id}`, updateData)
      // Si la respuesta es exitosa, el backend devuelve el viatger actualizado directamente
      // Lo envolvemos en el formato esperado
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error updating viatger:', error)

      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }

      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en actualitzar el viatger'
        }
      }

      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async delete(id: string): Promise<ViatgerResponse> {
    try {
      const response = await api.delete(`/viatgers/${id}`)
      // Si la respuesta es exitosa, el backend devuelve un mensaje o el objeto eliminado
      // Lo envolvemos en el formato esperado
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error deleting viatger:', error)
      throw error
    }
  }

  async validarDni(params: { num_document: string; reserva_id: string; viatger_id?: string }): Promise<{ valid: boolean; message?: string }> {
    try {
      const response = await api.post('/viatgers/validar-dni', params)
      return response.data
    } catch (error: any) {
      console.error('Error validating DNI:', error)
      throw error
    }
  }

  async getEstadistiques(): Promise<EstadistiquesResponse> {
    try {
      const response = await api.get('/viatgers/estadistiques')
      return response.data
    } catch (error: any) {
      console.error('Error fetching estadistiques:', error)
      throw error
    }
  }

  async generarFormulariReserva(reservaId: string): Promise<{ token: string; url: string }> {
    try {
      const response = await api.post('/forms/generar-formulari-reserva', { reserva_id: reservaId })
      return response.data
    } catch (error: any) {
      console.error('Error generating form:', error)
      throw error
    }
  }
}

export const ViatgersService = new ViatgersApiService()
